import {
  customFormatDate,
  formatNumber,
  getUniqueTooltipSeries
} from '../utils';

const getTooltipSeriesBaseKey = seriesLike =>
  seriesLike?.linkedTo ||
  seriesLike?.options?.linkedTo ||
  seriesLike?.linkedParent?.options?.id ||
  seriesLike?.id ||
  seriesLike?.options?.id ||
  seriesLike?.name ||
  seriesLike?.options?.name ||
  '';

export const colorsDashboard = [
  '#FF5733', // Vibrant red-orange
  '#3498DB', // Bright blue
  '#2ECC71', // Soft green
  '#F1C40F', // Golden yellow
  '#9B59B6', // Deep purple
  '#E74C3C', // Strong red
  '#1ABC9C', // Teal green
  '#D35400', // Burnt orange
  '#8E44AD', // Royal purple
  '#F39C12', // Warm yellow-orange
  '#2980B9', // Dark blue
  '#16A085', // Rich teal
  '#C0392B', // Dark red
  '#27AE60', // Lush green
  '#5D6D7E', // Slate gray-blue
  '#DFFF00', // Neon yellow-green
  '#1F618D', // Deep navy blue
  '#6C3483', // Dark violet
  '#117A65', // Dark turquoise
  '#2471A3', // Strong blue
  '#7DCEA0', // Soft pastel green
  '#FFB6C1', // Light pink
  '#8B0000', // Dark red
  '#FF4500', // Bright orange
  '#4682B4', // Steel blue
  '#2F4F4F', // Dark slate gray
  '#ADFF2F', // Green-yellow
  '#DA70D6', // Orchid purple
  '#FFD700', // Gold
  '#00CED1', // Dark turquoise
  '#DC143C', // Crimson red
  '#00FA9A', // Medium spring green
  '#FF69B4', // Hot pink
  '#6A5ACD', // Slate blue
  '#20B2AA', // Light sea green
  '#FFA07A', // Light salmon
  '#9370DB', // Medium purple
  '#40E0D0', // Turquoise
  '#A0522D', // Sienna
  '#7FFFD4', // Aquamarine
  '#BC8F8F', // Rosy brown
  '#4169E1', // Royal blue
  '#B0C4DE', // Light steel blue
  '#CD5C5C', // Indian red
  '#800000', // Maroon
  '#808000', // Olive
  '#00FF7F', // Spring green
  '#483D8B', // Dark slate blue
  '#B22222', // Firebrick
  '#00BFFF' // Deep sky blue
];

export const optionsPie = {
  chart: {
    type: 'pie'
  },
  title: {
    text: ''
  },
  tooltip: {
    pointFormat: '{series.name}: <b>{point.y:.0f} ({point.percentage:.0f}%)</b>'
  },
  credits: {
    enabled: false
  },
  colors: colorsDashboard.slice(1),
  plotOptions: {
    series: {
      innerSize: '50%',
      allowPointSelect: true,
      showInLegend: false,
      cursor: 'pointer',
      dataLabels: [
        {
          enabled: false,
          distance: 20
        },
        {
          enabled: true,
          distance: -40,
          format: '{point.percentage:.0f}%',
          style: {
            fontSize: '0.8em',
            textOutline: 'none',
            opacity: 0.7
          },
          filter: {
            operator: '>',
            property: 'percentage',
            value: 10
          }
        }
      ]
    }
  },
  series: [
    {
      name: 'Percentage',
      colorByPoint: true,
      data: []
    }
  ]
};

export const defaultOptionHighChart = {
  chart: {
    type: 'spline',
    zoomType: 'x'
  },
  colors: colorsDashboard,
  title: {
    text: 'Example'
  },
  subtitle: {
    text: 'Examcaaple'
  },
  xAxis: {
    categories: []
  },
  yAxis: {
    title: {
      text: ''
    }
  },
  credits: {
    enabled: false
  },
  series: [],
  plotOptions: {
    series: {
      // cumulative: true,
      events: {
        legendItemClick: function () {
          if (this.index === 0 && this.name === 'Show/Hide') {
            const chart = this.chart;
            const isVisible = this.visible;

            setTimeout(() => {
              chart.series.forEach((series, key) => {
                if (series.data && series.data.length > 0 && key > 0) {
                  if (isVisible) {
                    series.setVisible(false, false);
                  } else {
                    series.setVisible(true, false);
                  }
                }
              });
              chart.update({
                xAxis: {
                  labels: {
                    style: {
                      visibility: 'visible'
                    }
                  }
                },
                yAxis: {
                  labels: {
                    style: {
                      visibility: 'visible'
                    }
                  }
                }
              });
            }, 50);
          }
        }
      }
    }
  },
  legend: {
    align: 'center',
    verticalAlign: 'bottom',
    layout: 'horizontal',
    maxHeight: 70,
    navigation: {
      activeColor: '#3E576F',
      animation: true,
      arrowSize: 12,
      inactiveColor: '#CCC',
      style: {
        fontWeight: 'bold',
        color: '#333',
        fontSize: '12px'
      }
    }
  },
  tooltip: {
    shared: false,
    useHTML: true,
    outside: true,
    formatter: function () {
      const { series, point, x } = this;
      const { index } = point;
      const isDarkMode =
        typeof document !== 'undefined' &&
        document.body.classList.contains('dark');
      const tooltipBg = isDarkMode ? '#0f172a' : '#ffffff';
      const tooltipBorder = isDarkMode ? '#334155' : '#e2e8f0';
      const tooltipTitle = isDarkMode ? '#cbd5e1' : '#1f2937';
      const tooltipLabel = isDarkMode ? '#e2e8f0' : '#0f172a';
      const tooltipSecondary = isDarkMode ? '#94a3b8' : '#475569';

      const activeSeriesBaseKey = getTooltipSeriesBaseKey(series);
      const newSeries = getUniqueTooltipSeries(
        series.chart.options.series,
        index
      );

      const sortedArray = newSeries
        .map(item => ({
          ...item,
          isActive: getTooltipSeriesBaseKey(item) === activeSeriesBaseKey
        }))
        .sort((a, b) => {
          if (a.isActive) return -1;
          if (b.isActive) return 1;

          const aVal = a.data?.[index];
          const bVal = b.data?.[index];

          if (aVal == null && bVal == null) return 0;
          if (aVal == null) return 1;
          if (bVal == null) return -1;

          return bVal - aVal;
        });

      const activeItem = sortedArray.find(
        i => i.isActive && i.data?.[index] != null
      );
      const otherItems = sortedArray
        .filter(i => !i.isActive && i.data?.[index] != null)
        .slice(0, 40);

      const valueSuffix = this?.chart?.options?.chart?.isPercentageStacked
        ? '%'
        : '';
      const newElemenxt =
        `<div style="min-width:${sortedArray.length > 5 ? 400 : 200}px;background:${tooltipBg};border:1px solid ${tooltipBorder};border-radius:12px;padding:10px 12px;font-size:13px;box-shadow:0 10px 30px rgba(15,23,42,0.18);">` +
        `<div style="font-weight:700;margin-bottom:8px;color:${tooltipTitle};">${customFormatDate(x)}</div>` +
        (activeItem
          ? `
            <div style="display:flex;align-items:center;margin-bottom:8px;color:${tooltipLabel};">
              <span style="width:10px;height:10px;margin-right:8px;flex-shrink:0;background-color:${activeItem.color};border-radius:999px;"></span>
              <span style="font-weight:700;display:flex;align-items:center;gap:4px;max-width:300px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;" title="${activeItem.name}">
                ${activeItem.name}: ${formatNumber(activeItem.data[index])}${valueSuffix}
              </span>
            </div>`
          : '') +
        `<div style="display:grid;gap:4px 10px;grid-template-columns:${otherItems.length > 5 ? 'repeat(2, minmax(0, 1fr))' : '1fr'};">` +
        otherItems
          .map(
            p => `
            <div style="display:flex;align-items:center;color:${tooltipLabel};">
              <span style="width:10px;height:10px;margin-right:8px;flex-shrink:0;background-color:${p.color};border-radius:999px;"></span>
              <span style="max-width:120px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;display:inline-block;" title="${p.name}">
              ${p.name}:
            </span>
              <span style="margin-left:4px;color:${tooltipSecondary};">${formatNumber(p.data[index])}${valueSuffix}</span>
            </div>`
          )
          .join('') +
        `</div></div>`;

      return newElemenxt;
    }
  }
};

export const defaultOption = {
  legend: {
    show: true,
    position: 'top',
    horizontalAlign: 'left'
  },
  colors: ['#3C50E0', '#80CAEE'],
  chart: {
    fontFamily: 'Satoshi, sans-serif',
    height: 335,
    type: 'area',
    dropShadow: {
      enabled: true,
      color: '#623CEA14',
      top: 10,
      blur: 4,
      left: 0,
      opacity: 0.1
    },

    toolbar: {
      show: false
    }
  },
  responsive: [
    {
      breakpoint: 1024,
      options: {
        chart: {
          height: 300
        }
      }
    },
    {
      breakpoint: 1366,
      options: {
        chart: {
          height: 350
        }
      }
    }
  ],
  stroke: {
    width: [2, 2],
    curve: 'straight'
  },
  // labels: {
  //   show: false,
  //   position: "top",
  // },
  grid: {
    xaxis: {
      lines: {
        show: true
      }
    },
    yaxis: {
      lines: {
        show: true
      }
    }
  },
  dataLabels: {
    enabled: false
  },
  markerxs: {
    //size: 4,
    colors: '#fff',
    //strokeColors: ['#3056D3', '#80CAEE'],
    strokeWidth: 3,
    strokeOpacity: 0.9,
    strokeDashArray: 0,
    fillOpacity: 1,
    discrete: [],
    hover: {
      size: undefined,
      sizeOffset: 5
    }
  },
  xaxis: {
    type: 'category',
    categories: ['Sep', 'Oct'],
    axisBorder: {
      show: false
    },
    axisTicks: {
      show: false
    }
  },
  yaxis: {
    title: {
      style: {
        fontSize: '0px'
      }
    }
  }
};

export const donutOptions = {
  chart: {
    fontFamily: 'Satoshi, sans-serif',
    type: 'donut'
  },
  colors: ['#3C50E0', '#6577F3', '#8FD0EF', '#0FADCF'],
  labels: ['Desktop', 'Tablet', 'Mobile', 'Unknown'],
  legend: {
    show: false,
    position: 'bottom'
  },

  plotOptions: {
    pie: {
      donut: {
        size: '65%',
        background: 'transparent'
      }
    }
  },
  dataLabels: {
    enabled: false
  },
  responsive: [
    {
      breakpoint: 2600,
      options: {
        chart: {
          width: 380
        }
      }
    },
    {
      breakpoint: 640,
      options: {
        chart: {
          width: 200
        }
      }
    }
  ]
};
